/**
 * VouchersPage — Phase 8 · Accounting Voucher Ledger.
 *
 * Browse every voucher generated from daily sheets. Filters by date range,
 * type, and free-text party search. Top strip surfaces Section 40A(3)
 * disallowed cash spend so the owner/CA can see compliance exposure at
 * a glance.
 *
 * Route: /vouchers
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    Receipt,
    Search,
    AlertTriangle,
    Wallet,
    Banknote,
    Landmark,
    Users,
    CircleCheck,
    RefreshCw,
    Info,
    Download,
    Hash,
    FileCode,
    Trash2,
    Eye,
    Printer,
    Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageHeader } from '../components/PageHeader';
import { VoucherDetailDialog } from '../components/VoucherDetailDialog';
import { api, fmtMoney } from '../lib/api';


const VOUCHER_TYPES = [
    { key: 'ALL', label: 'All types' },
    { key: 'PAYMENT', label: 'Payment (Cash)' },
    { key: 'BANK_PAYMENT', label: 'Bank Payment' },
    { key: 'SALARY', label: 'Salary' },
    { key: 'DRAWINGS', label: 'Drawings' },
    { key: 'LOAN_ADVANCE', label: 'Advance' },
    { key: 'CAPEX', label: 'Capex' },
    { key: 'CONTRA', label: 'Contra' },
    { key: 'JOURNAL', label: 'Journal' },
];


function typeChip(t) {
    switch (t) {
        case 'PAYMENT':      return { icon: Wallet,    tone: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
        case 'BANK_PAYMENT': return { icon: Landmark,  tone: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' };
        case 'SALARY':       return { icon: Users,     tone: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300' };
        case 'DRAWINGS':     return { icon: Banknote,  tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' };
        case 'LOAN_ADVANCE': return { icon: Users,     tone: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300' };
        default:             return { icon: Receipt,   tone: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300' };
    }
}


export default function VouchersPage() {
    const [items, setItems] = useState(null);
    const [totals, setTotals] = useState({});
    const [breach, setBreach] = useState({ total_disallowed: 0, by_party: {} });
    const [type, setType] = useState('ALL');
    const [q, setQ] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null); // voucher object
    const [deleting, setDeleting] = useState(false);
    const [detailVoucherId, setDetailVoucherId] = useState(null); // opens VoucherDetailDialog
    const [selectedIds, setSelectedIds] = useState(() => new Set()); // ids of rows the user ticked
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const load = useCallback(async () => {
        setItems(null);
        try {
            const params = { limit: 500 };
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (type !== 'ALL') params.voucher_type = type;
            const [r, s] = await Promise.all([
                api.listVouchers(params),
                api.section40a3Report({}),
            ]);
            setItems(r.items || []);
            setTotals(r.totals || {});
            setBreach(s || { total_disallowed: 0, by_party: {} });
        } catch (e) {
            setItems([]);
            toast.error(e?.response?.data?.detail || 'Could not load vouchers.');
        }
    }, [fromDate, toDate, type]);

    useEffect(() => { load(); }, [load]);

    // Kick a CSV export using the current filter set. Uses a blob → object
    // URL → anchor click trick so downloads work cross-browser without
    // navigating away from the ledger.
    async function downloadCsv() {
        try {
            const params = {};
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (type !== 'ALL') params.voucher_type = type;
            const resp = await api.downloadVouchersCsv(params);
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vouchers_${fromDate || 'all'}_to_${toDate || 'now'}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const count = Number(resp.headers?.['x-voucher-count'] || 0);
            toast.success(`Downloaded ${count} voucher(s) as CSV.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'CSV download failed.');
        }
    }

    // Tally XML — same as CSV path but different endpoint + extension.
    // CA drops this file directly into TallyPrime → Import Data → Vouchers.
    async function downloadTallyXml() {
        try {
            const params = {};
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (type !== 'ALL') params.voucher_type = type;
            const resp = await api.downloadVouchersTallyXml(params);
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tally_vouchers_${fromDate || 'all'}_to_${toDate || 'now'}.xml`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const count = Number(resp.headers?.['x-voucher-count'] || 0);
            toast.success(`Downloaded ${count} voucher(s) for Tally.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Tally XML download failed.');
        }
    }

    // Bulk print — pops a new window with every voucher in the current
    // filter on its own A4 page, then fires the print dialog so the user
    // can Save as PDF (one file for all slips).
    const [bulkPrinting, setBulkPrinting] = useState(false);
    async function printAllAsPdf() {
        if (!items || items.length === 0) return;
        setBulkPrinting(true);
        try {
            const params = {};
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (type !== 'ALL') params.voucher_type = type;
            const res = await api.printVoucherBatch(params);
            toast.success(`Opened ${res.count} voucher(s) — pick "Save as PDF" in the print dialog.`);
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.detail || 'Bulk print failed.');
        } finally {
            setBulkPrinting(false);
        }
    }

    async function renumberAll() {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
            'Renumber every voucher with the correct per-type prefix '
            + '(PV / BP / SV / DV / LV)?\n\n'
            + 'Existing PV-XXX numbers will be re-issued to match voucher '
            + 'type. Safe to run — idempotent.',
        );
        if (!ok) return;
        try {
            const res = await api.renumberVouchers();
            toast.success(`Renumbered ${res.renumbered} voucher(s).`);
            if (res.sample_before?.length) {
                console.info('Sample changes:', res.sample_before.map((b, i) => `${b} → ${res.sample_after[i]}`));
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Renumber failed.');
        }
    }

    async function confirmDelete() {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            const res = await api.deleteVoucher(pendingDelete.id);
            const stockNote = res.stock_ledger_deleted
                ? ` · reversed ${res.stock_ledger_deleted} stock entry(ies)`
                : '';
            toast.success(`Voucher ${pendingDelete.voucher_number} deleted${stockNote}.`);
            setPendingDelete(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Delete failed.');
        } finally {
            setDeleting(false);
        }
    }

    // ── Bulk selection helpers ─────────────────────────────────────
    const toggleOne = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    async function confirmBulkDelete() {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        setBulkDeleting(true);
        try {
            const res = await api.bulkDeleteVouchers(ids);
            const bits = [`Deleted ${res.vouchers_deleted} voucher(s)`];
            if (res.stock_ledger_deleted) bits.push(`${res.stock_ledger_deleted} stock entry(ies) reversed`);
            if (res.sheets_unlinked) bits.push(`${res.sheets_unlinked} daily sheet(s) unlinked`);
            if (res.not_found?.length) bits.push(`${res.not_found.length} not found`);
            toast.success(bits.join(' · '));
            setBulkDeleteOpen(false);
            clearSelection();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Bulk delete failed.');
        } finally {
            setBulkDeleting(false);
        }
    }

    const filtered = useMemo(() => {
        if (!items) return null;
        const needle = q.trim().toLowerCase();
        if (!needle) return items;
        return items.filter((v) =>
            (v.party || '').toLowerCase().includes(needle)
            || (v.category || '').toLowerCase().includes(needle)
            || (v.voucher_number || '').toLowerCase().includes(needle)
            || (v.narration || '').toLowerCase().includes(needle),
        );
    }, [items, q]);

    const totalAll = useMemo(
        () => Object.values(totals || {}).reduce((s, v) => s + Number(v || 0), 0),
        [totals],
    );

    // ── Selection stats — kept as derived state so we don't drift out of
    // sync when the filter narrows and some previously-ticked rows drop
    // off the screen. `visibleSelected` counts only ids that are still
    // in view; that's what we surface in the selection bar.
    const filteredIds = useMemo(
        () => (filtered || []).map((v) => v.id).filter(Boolean),
        [filtered],
    );
    const visibleSelected = useMemo(
        () => filteredIds.filter((id) => selectedIds.has(id)),
        [filteredIds, selectedIds],
    );
    const selectionAmount = useMemo(() => {
        if (!filtered || visibleSelected.length === 0) return 0;
        const set = new Set(visibleSelected);
        return filtered.reduce(
            (s, v) => s + (set.has(v.id) ? Number(v.amount || 0) : 0),
            0,
        );
    }, [filtered, visibleSelected]);
    const allVisibleTicked = filteredIds.length > 0
        && visibleSelected.length === filteredIds.length;
    const someVisibleTicked = visibleSelected.length > 0 && !allVisibleTicked;

    const toggleAllVisible = useCallback(() => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (filteredIds.every((id) => next.has(id))) {
                // Uncheck only the visible rows — preserve any off-screen
                // selections from a broader filter the user just narrowed.
                filteredIds.forEach((id) => next.delete(id));
            } else {
                filteredIds.forEach((id) => next.add(id));
            }
            return next;
        });
    }, [filteredIds]);

    return (
        <div className="max-w-6xl mx-auto py-6 px-4 space-y-4" data-testid="vouchers-page">
            <PageHeader
                title="Accounting vouchers"
                subtitle="Every daily-sheet expense becomes one voucher. Filter, review, and export for your CA."
                icon={Receipt}
            />

            {/* ── KPI strip ─────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-3" data-testid="voucher-kpi-count">
                    <div className="text-[10px] uppercase text-muted-foreground">Vouchers ({items?.length ?? '—'})</div>
                    <div className="font-heading text-xl mt-0.5 font-mono tabular-nums">{fmtMoney(totalAll)}</div>
                </Card>
                <Card className="p-3" data-testid="voucher-kpi-cash">
                    <div className="text-[10px] uppercase text-muted-foreground">Cash payments</div>
                    <div className="font-heading text-xl mt-0.5 font-mono tabular-nums">{fmtMoney(totals?.PAYMENT || 0)}</div>
                </Card>
                <Card className="p-3" data-testid="voucher-kpi-bank">
                    <div className="text-[10px] uppercase text-muted-foreground">Bank / UPI</div>
                    <div className="font-heading text-xl mt-0.5 font-mono tabular-nums">{fmtMoney(totals?.BANK_PAYMENT || 0)}</div>
                </Card>
                <Card
                    className={`p-3 ${breach.total_disallowed > 0 ? 'border-red-300 bg-red-50/60 dark:bg-red-950/20' : ''}`}
                    data-testid="voucher-kpi-40a3"
                >
                    <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
                        Section 40A(3) disallowed
                        <span title="Cash payments > ₹10,000 per party per day — cannot be claimed as business expense (Income Tax Act, Sec 40A(3))">
                            <Info size={11} />
                        </span>
                    </div>
                    <div
                        className={`font-heading text-xl mt-0.5 font-mono tabular-nums ${breach.total_disallowed > 0 ? 'text-red-700 dark:text-red-400' : ''}`}
                    >
                        {fmtMoney(breach.total_disallowed || 0)}
                    </div>
                    {Object.keys(breach.by_party || {}).length > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                            {Object.keys(breach.by_party).length} party (parties) affected
                        </div>
                    )}
                </Card>
            </div>

            {/* ── Filters ────────────────────────────────────────── */}
            <Card className="p-3 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] uppercase text-muted-foreground block mb-1">Search</label>
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="party, category, voucher #…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-8 h-9"
                            data-testid="voucher-search"
                        />
                    </div>
                </div>
                <div className="w-40">
                    <label className="text-[10px] uppercase text-muted-foreground block mb-1">Type</label>
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger className="h-9" data-testid="voucher-filter-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {VOUCHER_TYPES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-36">
                    <label className="text-[10px] uppercase text-muted-foreground block mb-1">From</label>
                    <Input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="h-9"
                        data-testid="voucher-filter-from"
                    />
                </div>
                <div className="w-36">
                    <label className="text-[10px] uppercase text-muted-foreground block mb-1">To</label>
                    <Input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="h-9"
                        data-testid="voucher-filter-to"
                    />
                </div>
                <Button variant="outline" onClick={load} className="gap-1 h-9" data-testid="voucher-refresh">
                    <RefreshCw size={13} /> Refresh
                </Button>
                <Button
                    variant="outline"
                    onClick={renumberAll}
                    className="gap-1 h-9"
                    title="Renumber every voucher with the correct per-type prefix (PV / BP / SV / DV / LV). Safe to run."
                    data-testid="voucher-renumber"
                >
                    <Hash size={13} /> Fix numbering
                </Button>
                <Button
                    onClick={downloadCsv}
                    disabled={!items || items.length === 0}
                    className="gap-1 h-9"
                    variant="outline"
                    data-testid="voucher-download-csv"
                >
                    <Download size={13} /> CSV
                </Button>
                <Button
                    onClick={downloadTallyXml}
                    disabled={!items || items.length === 0}
                    className="gap-1 h-9"
                    title="Download as TallyPrime-compatible XML — your CA imports directly into Tally"
                    data-testid="voucher-download-tally"
                >
                    <FileCode size={13} /> Tally XML
                </Button>
                <Button
                    onClick={printAllAsPdf}
                    disabled={!items || items.length === 0 || bulkPrinting}
                    className="gap-1 h-9"
                    variant="outline"
                    title="Combine every filtered voucher into one printable PDF (one slip per page)"
                    data-testid="voucher-print-all"
                >
                    {bulkPrinting ? (
                        <><Loader2 size={13} className="animate-spin" /> Preparing…</>
                    ) : (
                        <><Printer size={13} /> Print all as PDF</>
                    )}
                </Button>
            </Card>

            {/* ── Selection action bar (sticky just above the table) ── */}
            {visibleSelected.length > 0 && (
                <div
                    className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/40 bg-primary/10 backdrop-blur px-3 py-2 shadow-sm"
                    data-testid="voucher-selection-bar"
                >
                    <div className="text-sm flex items-center gap-3">
                        <span className="font-semibold" data-testid="voucher-selection-count">
                            {visibleSelected.length} selected
                        </span>
                        <span className="text-muted-foreground">
                            Total: <span className="font-mono">{fmtMoney(selectionAmount)}</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8"
                            onClick={clearSelection}
                            data-testid="voucher-selection-clear"
                        >
                            Clear
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            className="h-8 gap-1"
                            onClick={() => setBulkDeleteOpen(true)}
                            data-testid="voucher-bulk-delete"
                        >
                            <Trash2 size={13} /> Delete selected
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Table ─────────────────────────────────────────── */}
            <Card className="p-0 overflow-hidden">
                {filtered === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                            <Receipt size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No vouchers yet</div>
                        <div className="text-sm text-muted-foreground max-w-md mx-auto">
                            Vouchers are generated when you save a daily sheet and convert it,
                            or run "Backfill vouchers" on the Daily Sheet History page.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground text-[11px] uppercase tracking-wide">
                                <tr>
                                    <th className="px-3 py-2 w-8">
                                        <Checkbox
                                            checked={allVisibleTicked ? true : (someVisibleTicked ? 'indeterminate' : false)}
                                            onCheckedChange={toggleAllVisible}
                                            aria-label="Select all visible vouchers"
                                            data-testid="voucher-select-all"
                                        />
                                    </th>
                                    <th className="text-left px-3 py-2 font-medium">Voucher #</th>
                                    <th className="text-left px-3 py-2 font-medium">Date</th>
                                    <th className="text-left px-3 py-2 font-medium">Type</th>
                                    <th className="text-left px-3 py-2 font-medium">Party</th>
                                    <th className="text-left px-3 py-2 font-medium">Category</th>
                                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                                    <th className="text-left px-3 py-2 font-medium">Mode</th>
                                    <th className="text-left px-3 py-2 font-medium">Flag</th>
                                    <th className="text-right px-3 py-2 font-medium w-20">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((v, i) => {
                                    const chip = typeChip(v.voucher_type);
                                    const Icon = chip.icon;
                                    const disallowed = v.compliance_flag === 'SECTION_40A3_DISALLOWED';
                                    const isSelected = selectedIds.has(v.id);
                                    return (
                                        <tr
                                            key={v.id}
                                            className={`border-t border-border hover:bg-muted/30 ${disallowed ? 'bg-red-50/40 dark:bg-red-950/10' : ''} ${isSelected ? 'bg-primary/5' : ''}`}
                                            data-testid={`voucher-row-${i}`}
                                        >
                                            <td className="px-3 py-2">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleOne(v.id)}
                                                    aria-label={`Select voucher ${v.voucher_number}`}
                                                    data-testid={`voucher-select-${i}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs">{v.voucher_number}</td>
                                            <td className="px-3 py-2 text-xs text-muted-foreground">{v.date}</td>
                                            <td className="px-3 py-2">
                                                <span
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${chip.tone}`}
                                                >
                                                    <Icon size={10} /> {v.voucher_type}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 font-medium">{v.party}</td>
                                            <td className="px-3 py-2 text-xs text-muted-foreground">{v.category}</td>
                                            <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">
                                                {fmtMoney(v.amount)}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                <Badge variant="outline" className="text-[10px]">{v.payment_mode}</Badge>
                                            </td>
                                            <td className="px-3 py-2">
                                                {disallowed ? (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-red-700 dark:text-red-400 text-[10px] font-medium"
                                                        title="Cash payment > ₹10,000/day — cannot be claimed as business expense"
                                                    >
                                                        <AlertTriangle size={11} /> 40A(3)
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-[10px]">
                                                        <CircleCheck size={11} /> OK
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <div className="inline-flex items-center gap-0.5">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                                        onClick={() => setDetailVoucherId(v.id)}
                                                        title="View formal voucher slip"
                                                        data-testid={`voucher-view-${i}`}
                                                    >
                                                        <Eye size={13} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => setPendingDelete(v)}
                                                        title="Delete this voucher"
                                                        data-testid={`voucher-delete-${i}`}
                                                    >
                                                        <Trash2 size={13} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <div className="text-xs text-muted-foreground text-center">
                Showing {filtered?.length ?? 0} voucher(s){q ? ` matching "${q}"` : ''}.
                {items && items.length > 0 && ' Click Refresh after adding new expenses.'}
            </div>

            {/* ── Voucher detail (Tally-style slip) ────────────────── */}
            <VoucherDetailDialog
                voucherId={detailVoucherId}
                open={!!detailVoucherId}
                onOpenChange={(o) => { if (!o) setDetailVoucherId(null); }}
            />

            {/* ── Delete confirmation ─────────────────────────────── */}
            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
            >
                <AlertDialogContent data-testid="voucher-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete voucher {pendingDelete?.voucher_number}?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm text-muted-foreground">
                                <div>
                                    <span className="font-medium text-foreground">{pendingDelete?.party}</span>
                                    {' — '}
                                    <span className="font-mono">{fmtMoney(pendingDelete?.amount || 0)}</span>
                                    {' · '}
                                    {pendingDelete?.date}
                                </div>
                                <div>
                                    This will permanently delete the voucher and reverse any
                                    stock ledger entries it created. If it was generated from a
                                    daily sheet, it will be unlinked so the sheet can regenerate
                                    vouchers later.
                                </div>
                                <div className="text-amber-700 dark:text-amber-400 text-xs">
                                    This action cannot be undone.
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="voucher-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="voucher-delete-confirm"
                        >
                            {deleting ? 'Deleting…' : 'Delete voucher'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* ── Bulk delete confirmation ─────────────────────────── */}
            <AlertDialog
                open={bulkDeleteOpen}
                onOpenChange={(o) => { if (!o) setBulkDeleteOpen(false); }}
            >
                <AlertDialogContent data-testid="voucher-bulk-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete {visibleSelected.length} voucher(s)?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-muted-foreground">
                                Total value: <span className="font-mono font-semibold text-foreground">{fmtMoney(selectionAmount)}</span>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-1">
                        <div className="font-medium text-foreground">Will be removed automatically:</div>
                        <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                            <li>All <span className="font-medium text-foreground">{visibleSelected.length} voucher(s)</span> you selected</li>
                            <li>Any <span className="font-medium text-foreground">stock ledger entries</span> those vouchers created</li>
                            <li>Backlinks in the source daily sheets so they can regenerate later</li>
                        </ul>
                        <div className="text-amber-700 dark:text-amber-400 pt-1">
                            This action cannot be undone.
                        </div>
                    </div>

                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="voucher-bulk-delete-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmBulkDelete}
                            disabled={bulkDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="voucher-bulk-delete-confirm"
                        >
                            {bulkDeleting ? 'Deleting…' : `Delete ${visibleSelected.length} voucher(s)`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
